import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

// Icon assets from Figma
const healMeLogo = "http://localhost:3845/assets/3e03f0752b002ce5aa539071720037986755f13f.png"

/**
 * CredentialsSection component that displays practitioner credentials
 * Matches the Figma design with proper theme integration
 *
 * @param {Object} section - Section configuration
 * @param {Object} practice - Practice data (optional, for fallback credentials)
 * @param {Object} theme - Theme configuration
 * @param {string} highlightedField - Currently highlighted field
 */

const CredentialsSection = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Get credentials from settings or fallback to practice credentials
  const getCredentials = () => {
    if (settings.credentials && settings.credentials.length > 0) {
      return settings.credentials
    }

    // Fallback to practice credentials if available
    if (practice?.proCredentials && practice.proCredentials.length > 0) {
      return practice.proCredentials.map((cred, index) => ({
        id: cred.id || index,
        title: cred.credential?.name || cred.name || "Credential",
        organization: cred.issuer?.name || cred.issuer_name || ""
      }))
    }

    return []
  }

  const credentials = getCredentials()

  const renderCredential = (credential) => (
    <div
      key={credential.id}
      className="relative box-border flex w-full shrink-0 content-stretch items-center justify-start gap-6 px-6 py-5"
      style={{ backgroundColor: theme?.colors?.secondary || theme?.colors?.bgLight || "#F9F0E7" }}>
      <div className="relative flex w-full shrink-0 content-stretch items-center justify-start gap-4">
        {/* License Icon */}
        <div className="relative size-8 shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 26 26"
            fill="none"
            className="h-full w-full">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.47067 2.06534C9.03328 1.41631 9.72897 0.895942 10.5105 0.539575C11.292 0.183209 12.1411 -0.000813749 13 2.70491e-06C14.8093 2.70491e-06 16.4307 0.800003 17.5293 2.06534C18.3863 2.00415 19.2463 2.12811 20.0511 2.42881C20.8559 2.72951 21.5865 3.19989 22.1933 3.808C22.8012 4.41475 23.2714 5.14517 23.5721 5.94968C23.8728 6.75419 23.9969 7.61397 23.936 8.47067C24.5848 9.03341 25.1049 9.72915 25.461 10.5107C25.8172 11.2922 26.001 12.1412 26 13C26.0008 13.8589 25.8168 14.708 25.4604 15.4895C25.1041 16.271 24.5837 16.9667 23.9347 17.5293C23.9956 18.386 23.8715 19.2458 23.5708 20.0503C23.2701 20.8548 22.7999 21.5852 22.192 22.192C21.5853 22.7999 20.8548 23.2701 20.0503 23.5708C19.2458 23.8715 18.386 23.9956 17.5293 23.9347C16.9667 24.5837 16.271 25.1041 15.4895 25.4604C14.708 25.8168 13.8589 26.0008 13 26C12.1411 26.0008 11.292 25.8168 10.5105 25.4604C9.72897 25.1041 9.03328 24.5837 8.47067 23.9347C7.61384 23.996 6.75384 23.8723 5.94908 23.5718C5.14431 23.2713 4.41364 22.8012 3.80667 22.1933C3.19863 21.5864 2.72829 20.8558 2.4276 20.051C2.12691 19.2463 2.00291 18.3863 2.064 17.5293C1.41522 16.9666 0.895103 16.2709 0.53897 15.4893C0.182837 14.7078 -0.00097815 13.8588 3.91446e-06 13C3.91446e-06 11.1907 0.800004 9.56933 2.06534 8.47067C2.00435 7.61396 2.12841 6.75415 2.4291 5.94963C2.72979 5.14511 3.20006 4.4147 3.808 3.808C4.4147 3.20006 5.14511 2.72978 5.94963 2.4291C6.75416 2.12841 7.61396 2.00434 8.47067 2.06534ZM17.8133 10.5813C17.8933 10.4747 17.9512 10.3532 17.9836 10.2239C18.016 10.0946 18.0221 9.96019 18.0018 9.82847C17.9815 9.69676 17.935 9.57043 17.8651 9.45693C17.7953 9.34343 17.7034 9.24504 17.595 9.16754C17.4866 9.09004 17.3638 9.03499 17.2337 9.00564C17.1037 8.97629 16.9692 8.97322 16.838 8.99662C16.7068 9.02002 16.5816 9.0694 16.4697 9.14188C16.3579 9.21436 16.2616 9.30847 16.1867 9.41867L11.872 15.4587L9.70667 13.2933C9.5171 13.1167 9.26638 13.0205 9.00731 13.0251C8.74824 13.0297 8.50106 13.1346 8.31784 13.3178C8.13462 13.5011 8.02967 13.7482 8.0251 14.0073C8.02053 14.2664 8.1167 14.5171 8.29334 14.7067L11.2933 17.7067C11.396 17.8092 11.5197 17.8882 11.656 17.9382C11.7922 17.9881 11.9377 18.0078 12.0824 17.9959C12.227 17.984 12.3673 17.9407 12.4935 17.8691C12.6197 17.7975 12.7289 17.6993 12.8133 17.5813L17.8133 10.5813Z"
              fill={theme?.colors?.primary}
            />
          </svg>
        </div>

        {/* Text Content */}
        <div className="relative flex min-h-px min-w-px shrink-0 grow basis-0 flex-col content-stretch items-start justify-start leading-[0]">
          <div
            className="relative min-w-full shrink-0 text-[18px] not-italic"
            style={{
              fontFamily: theme?.fonts?.paragraph,
              fontWeight: theme?.fonts?.paragraphBoldWeight,
              lineHeight: "32px",
              color: theme?.colors?.textDark
            }}>
            <p className="leading-[32px]">{credential.title}</p>
          </div>

          {credential.organization && (
            <div
              className="relative w-full shrink-0 text-[14px] font-normal"
              style={{
                fontFamily: theme?.fonts?.paragraph,
                fontWeight: theme?.fonts?.paragraphRegularWeight,
                lineHeight: "20px",
                color: theme?.colors?.textDark
              }}>
              <p className="leading-[20px]">{credential.organization}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderVerifiedBadge = () => (
    <div className="relative flex shrink-0 content-stretch items-center justify-start gap-4">
      <div className="relative flex size-8 shrink-0 content-stretch items-center justify-start gap-2.5 text-clip rounded-[4px]">
        <div
          className="h-full min-h-px min-w-px shrink-0 grow basis-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${healMeLogo}')` }}
        />
      </div>

      <div className="relative box-border flex shrink-0 content-stretch items-center justify-center gap-1 text-clip rounded-[26px] px-0 py-1">
        <div
          className="relative flex shrink-0 flex-col justify-center text-nowrap font-['Work_Sans:Semibold',_sans-serif] text-[12px] uppercase not-italic leading-[0] tracking-[0.75px]"
          style={{
            fontFamily: theme?.fonts?.paragraph,
            fontWeight: theme?.fonts?.paragraphBoldWeight,
            lineHeight: "16px",
            color: theme?.colors?.textDark
          }}>
          <p className="whitespace-pre leading-[16px]">Verified by heal.me</p>
        </div>
      </div>

      <div className="absolute left-[22px] top-5 size-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 26 26"
          fill="none"
          className="h-full w-full">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.47067 2.06534C9.03328 1.41631 9.72897 0.895942 10.5105 0.539575C11.292 0.183209 12.1411 -0.000813749 13 2.70491e-06C14.8093 2.70491e-06 16.4307 0.800003 17.5293 2.06534C18.3863 2.00415 19.2463 2.12811 20.0511 2.42881C20.8559 2.72951 21.5865 3.19989 22.1933 3.808C22.8012 4.41475 23.2714 5.14517 23.5721 5.94968C23.8728 6.75419 23.9969 7.61397 23.936 8.47067C24.5848 9.03341 25.1049 9.72915 25.461 10.5107C25.8172 11.2922 26.001 12.1412 26 13C26.0008 13.8589 25.8168 14.708 25.4604 15.4895C25.1041 16.271 24.5837 16.9667 23.9347 17.5293C23.9956 18.386 23.8715 19.2458 23.5708 20.0503C23.2701 20.8548 22.7999 21.5852 22.192 22.192C21.5853 22.7999 20.8548 23.2701 20.0503 23.5708C19.2458 23.8715 18.386 23.9956 17.5293 23.9347C16.9667 24.5837 16.271 25.1041 15.4895 25.4604C14.708 25.8168 13.8589 26.0008 13 26C12.1411 26.0008 11.292 25.8168 10.5105 25.4604C9.72897 25.1041 9.03328 24.5837 8.47067 23.9347C7.61384 23.996 6.75384 23.8723 5.94908 23.5718C5.14431 23.2713 4.41364 22.8012 3.80667 22.1933C3.19863 21.5864 2.72829 20.8558 2.4276 20.051C2.12691 19.2463 2.00291 18.3863 2.064 17.5293C1.41522 16.9666 0.895103 16.2709 0.53897 15.4893C0.182837 14.7078 -0.00097815 13.8588 3.91446e-06 13C3.91446e-06 11.1907 0.800004 9.56933 2.06534 8.47067C2.00435 7.61396 2.12841 6.75415 2.4291 5.94963C2.72979 5.14511 3.20006 4.4147 3.808 3.808C4.4147 3.20006 5.14511 2.72978 5.94963 2.4291C6.75416 2.12841 7.61396 2.00434 8.47067 2.06534ZM17.8133 10.5813C17.8933 10.4747 17.9512 10.3532 17.9836 10.2239C18.016 10.0946 18.0221 9.96019 18.0018 9.82847C17.9815 9.69676 17.935 9.57043 17.8651 9.45693C17.7953 9.34343 17.7034 9.24504 17.595 9.16754C17.4866 9.09004 17.3638 9.03499 17.2337 9.00564C17.1037 8.97629 16.9692 8.97322 16.838 8.99662C16.7068 9.02002 16.5816 9.0694 16.4697 9.14188C16.3579 9.21436 16.2616 9.30847 16.1867 9.41867L11.872 15.4587L9.70667 13.2933C9.5171 13.1167 9.26638 13.0205 9.00731 13.0251C8.74824 13.0297 8.50106 13.1346 8.31784 13.3178C8.13462 13.5011 8.02967 13.7482 8.0251 14.0073C8.02053 14.2664 8.1167 14.5171 8.29334 14.7067L11.2933 17.7067C11.396 17.8092 11.5197 17.8882 11.656 17.9382C11.7922 17.9881 11.9377 18.0078 12.0824 17.9959C12.227 17.984 12.3673 17.9407 12.4935 17.8691C12.6197 17.7975 12.7289 17.6993 12.8133 17.5813L17.8133 10.5813Z"
            fill={theme?.colors?.primary}
          />
        </svg>
      </div>
    </div>
  )

  const renderContent = () => (
    <div className="relative flex w-full shrink-0 flex-col content-stretch items-center justify-start gap-10 px-4">
      {/* Title - Always show "Credentials" */}
      <div
        className="relative shrink-0 text-nowrap text-center font-['Playfair_Display:Bold',_sans-serif] text-[40px] font-bold leading-[0]"
        style={{
          fontFamily: theme?.fonts?.heading,
          fontWeight: theme?.fonts?.headingWeight,
          lineHeight: "48px",
          color: theme?.colors?.textDark
        }}>
        <p className="whitespace-pre leading-[48px]">Credentials</p>
      </div>

      {/* Credentials List */}
      {credentials.length > 0 ? (
        <div
          className="relative flex w-full shrink-0 flex-col content-stretch items-start justify-start"
          style={{ gap: "12px" }}>
          {credentials.map(renderCredential)}
        </div>
      ) : (
        <div className="py-8 text-center">
          <p
            className="text-lg"
            style={{
              fontFamily: theme?.fonts?.paragraph,
              color: theme?.colors?.textDark
            }}>
            No credentials available
          </p>
        </div>
      )}

      {/* Verified Badge */}
      {credentials.length > 0 && renderVerifiedBadge()}
    </div>
  )

  return (
    <section
      className={twMerge(
        "relative box-border flex size-full flex-col content-stretch items-center justify-start gap-8 px-0 py-16",
        isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
      )}
      style={{ backgroundColor: theme?.colors?.white }}>
      <AnimatedSection className="flex w-full max-w-[720px] flex-col items-center justify-start gap-8">
        {renderContent()}
      </AnimatedSection>
    </section>
  )
}

export default CredentialsSection
